import type { StockStatus } from '@features/inventory';

/**
 * What a new picklist can actually take, as opposed to what NetSuite says is on hand (14ykpqtv7nd).
 *
 * NetSuite counts a picklist's units as on hand until its MIS posts, so raw on-hand overstates. The cart's plan has
 * always planned from `onHand − committed` (`itemPlan.ts`); the stock pill, the Out filter and Allocate Bins read raw
 * on-hand, which is how one screen said "In Stock", "Short 1" and "2 over" about one rack whose 2 units another
 * picklist held. These are the pure halves that make them agree.
 *
 * `stockStatusOf` (`stockStore.ts`) is deliberately left alone: its tests pin the raw meaning, and the max-quantity
 * clamp and the Bins button read it.
 */

/**
 * The pill's status from free stock: raw on-hand less what other open picklists hold.
 *
 * Only an "available" or "low" verdict is re-judged — loading, error, unknown and a raw out-of-stock already say
 * everything. While the committed read is still in flight the pill says Checking rather than showing raw stock that
 * would flip to Out of Stock a moment later; if that read failed or never ran, the raw verdict stands rather than a
 * guess.
 */
export function freeStockStatus(
  raw: StockStatus,
  onHand: number | null | undefined,
  committed: number | null | undefined,
  committedLoading: boolean,
  wanted: number,
): StockStatus {
  if (raw !== 'available' && raw !== 'low') return raw;
  if (committed == null) return committedLoading ? 'loading' : raw;
  if (onHand == null) return raw;

  const free = Math.max(0, onHand - committed);
  if (free <= 0) return 'out_of_stock';
  if (free < wanted) return 'low';
  return 'available';
}

/**
 * The one sentence under the stock popover's figures (owner, 2026-10-15: "understand it in one glance"). Says what the
 * figures MEAN for this line, in the words the cart's Short popover already uses. `null` when there is nothing to
 * judge (stock not read, or a state that is not about quantity).
 */
export function stockVerdict(
  status: StockStatus,
  onHand: number | undefined,
  held: number,
  wanted: number | undefined,
): string | null {
  if (onHand === undefined || wanted === undefined) return null;
  const free = Math.max(0, onHand - held);
  switch (status) {
    case 'out_of_stock':
      return onHand > 0 && held > 0
        ? 'Everything on hand is held by other open picklists. This line waits for the next picklist.'
        : 'Nothing on hand. This line waits for stock.';
    case 'low':
      return `Only ${free} free of the ${wanted} wanted. The picklist takes what is free; the rest waits for stock.`;
    case 'available':
      return held > 0 ? 'Enough is free for this line after what other picklists hold.' : 'Enough on hand for this line.';
    default:
      return null;
  }
}

/** The subset of a bin row the hold split reads. */
export interface HoldRow {
  binId: number;
  quantityAvailable: number;
}

export interface BinHoldSplit<T extends HoldRow> {
  /** Rows with something free, each reduced to what is free. What may be allocated. */
  free: T[];
  /** Rows other picklists hold entirely, at their raw quantity. Shown behind "Show held bins", never chosen. */
  held: T[];
  /** Units free across every row. */
  freeUnits: number;
  /** Units other picklists hold across every row (never more than the rows have). */
  heldUnits: number;
}

/**
 * Take what other open picklists hold off a lot or untracked item's bin rows (14ykpqtv7nd).
 *
 * `holdsByBin` is the server's per-bin figure — the same one creation checks a hand-picked claim against — so the
 * dialog offers exactly what a save accepts. A bin's hold is spent across its rows in order, mirroring the server's
 * step 7b, and a row is never reduced below zero. Serialized items do not come through here: their held serials are
 * split off by name (`splitByCommitment`).
 *
 * ⚡ Returns the SAME array as `free` when nothing is held, for the reason `deductHoldings` does: the dialog memoises
 * on the row reference.
 */
export function splitByBinHolds<T extends HoldRow>(
  rows: T[],
  holdsByBin: ReadonlyMap<number, number> | undefined,
): BinHoldSplit<T> {
  const live = rows.filter((r) => r.quantityAvailable > 0);
  if (!holdsByBin?.size) {
    const freeUnits = live.reduce((sum, r) => sum + r.quantityAvailable, 0);
    return { free: live.length === rows.length ? rows : live, held: [], freeUnits, heldUnits: 0 };
  }

  const remaining = new Map(holdsByBin);
  const free: T[] = [];
  const held: T[] = [];
  let freeUnits = 0;
  let heldUnits = 0;

  for (const row of live) {
    const owed = remaining.get(row.binId) ?? 0;
    const taken = Math.min(owed, row.quantityAvailable);
    if (taken > 0) remaining.set(row.binId, owed - taken);
    heldUnits += taken;

    const left = row.quantityAvailable - taken;
    if (left > 0) {
      free.push(taken > 0 ? { ...row, quantityAvailable: left } : row);
      freeUnits += left;
    } else {
      held.push(row);
    }
  }

  return { free, held, freeUnits, heldUnits };
}

/** The dialog's caption over a lot or untracked item's bins, in the serial caption's shape ("1 free · 2 on other picklists"). */
export function binHoldCaption(freeUnits: number, heldUnits: number, unit: string): string {
  return `${freeUnits} ${unit} free · ${heldUnits} on other picklists`;
}
